/**
 * Client Document Vault Component
 * Document management system for individual client profiles
 * Features: Folder structure, Upload, Status tracking, Expiration alerts, Search
 * 
 * Access Levels:
 * - Admin: Full access (view, download, upload, delete, edit notes)
 * - Client: Limited access (view, download, upload only - NO delete)
 */

import { useState, useCallback } from 'react';
import { 
  FolderOpen, FileText, Upload, Download, Search, 
  AlertTriangle, CheckCircle, Clock, Plus, X, Eye,
  Building, ScrollText, Shield, Truck, DollarSign, Users,
  Calendar, Edit, File, FileImage, AlertCircle
} from 'lucide-react';

// Document status configuration
const STATUS_CONFIG = {
  uploaded: { label: 'Uploaded', color: '#10B981', bg: 'bg-green-500/20', icon: CheckCircle },
  pending: { label: 'Pending', color: '#F59E0B', bg: 'bg-yellow-500/20', icon: Clock },
  missing: { label: 'Missing', color: '#EF4444', bg: 'bg-red-500/20', icon: AlertCircle },
  expiring: { label: 'Expiring Soon', color: '#F59E0B', bg: 'bg-orange-500/20', icon: AlertTriangle }
};

// Folder definitions for client vault
const CLIENT_FOLDERS = [
  { id: 'all', name: 'All Documents', icon: FolderOpen, color: '#D4AF37' },
  { id: 'business', name: 'Business Documents', icon: Building, color: '#8B5CF6' },
  { id: 'authority', name: 'Authority Documents', icon: ScrollText, color: '#3B82F6' },
  { id: 'insurance', name: 'Insurance', icon: Shield, color: '#10B981' },
  { id: 'compliance', name: 'Compliance', icon: CheckCircle, color: '#22C55E' },
  { id: 'driver', name: 'Driver Documents', icon: Users, color: '#F59E0B' },
  { id: 'financial', name: 'Financial / Operations', icon: DollarSign, color: '#EC4899' }
];

const ClientDocumentVault = ({ client, isAdmin = true, pkgColor = '#D4AF37' }) => {
  const [activeFolder, setActiveFolder] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Mock documents for this client
  const [documents, setDocuments] = useState([
    // Business Documents
    {
      id: 'CDOC-001',
      name: 'Certificate of Formation.pdf',
      folder: 'business',
      type: 'pdf',
      size: '1.2 MB',
      status: 'uploaded',
      uploadedBy: 'Admin',
      uploadedAt: '2026-02-15',
      expiresAt: null,
      expirationDays: null,
      notes: 'Original formation documents'
    },
    {
      id: 'CDOC-002',
      name: 'EIN Confirmation Letter.pdf',
      folder: 'business',
      type: 'pdf',
      size: '245 KB',
      status: 'uploaded',
      uploadedBy: 'Client',
      uploadedAt: '2026-02-16',
      expiresAt: null,
      expirationDays: null,
      notes: ''
    },
    {
      id: 'CDOC-003',
      name: 'Business License.pdf',
      folder: 'business',
      type: 'pdf',
      size: '890 KB',
      status: 'expiring',
      uploadedBy: 'Admin',
      uploadedAt: '2025-04-01',
      expiresAt: '2026-04-01',
      expirationDays: 12,
      notes: 'Renewal required by April 1, 2026'
    },
    // Authority Documents
    {
      id: 'CDOC-004',
      name: 'MC Authority Certificate.pdf',
      folder: 'authority',
      type: 'pdf',
      size: '567 KB',
      status: 'uploaded',
      uploadedBy: 'Admin',
      uploadedAt: '2026-01-20',
      expiresAt: null,
      expirationDays: null,
      notes: 'Active MC authority confirmed'
    },
    {
      id: 'CDOC-005',
      name: 'DOT Registration.pdf',
      folder: 'authority',
      type: 'pdf',
      size: '445 KB',
      status: 'uploaded',
      uploadedBy: 'Admin',
      uploadedAt: '2026-01-20',
      expiresAt: null,
      expirationDays: null,
      notes: ''
    },
    {
      id: 'CDOC-006',
      name: 'BOC-3 Filing.pdf',
      folder: 'authority',
      type: 'pdf',
      size: '234 KB',
      status: 'pending',
      uploadedBy: null,
      uploadedAt: null,
      expiresAt: null,
      expirationDays: null,
      notes: 'Awaiting upload from carrier'
    },
    // Insurance
    {
      id: 'CDOC-007',
      name: 'Auto Liability Insurance.pdf',
      folder: 'insurance',
      type: 'pdf',
      size: '2.1 MB',
      status: 'uploaded',
      uploadedBy: 'Client',
      uploadedAt: '2026-01-15',
      expiresAt: '2026-07-15',
      expirationDays: 117,
      notes: '$1M coverage - Progressive'
    },
    {
      id: 'CDOC-008',
      name: 'Cargo Insurance.pdf',
      folder: 'insurance',
      type: 'pdf',
      size: '1.8 MB',
      status: 'uploaded',
      uploadedBy: 'Client',
      uploadedAt: '2026-01-15',
      expiresAt: '2026-07-15',
      expirationDays: 117,
      notes: '$100K cargo coverage'
    },
    {
      id: 'CDOC-009',
      name: 'Workers Comp Certificate.pdf',
      folder: 'insurance',
      type: 'pdf',
      size: '0 KB',
      status: 'missing',
      uploadedBy: null,
      uploadedAt: null,
      expiresAt: null,
      expirationDays: null,
      notes: 'Required for compliance'
    },
    // Compliance
    {
      id: 'CDOC-010',
      name: 'Drug & Alcohol Policy.pdf',
      folder: 'compliance',
      type: 'pdf',
      size: '567 KB',
      status: 'uploaded',
      uploadedBy: 'Admin',
      uploadedAt: '2026-02-01',
      expiresAt: null,
      expirationDays: null,
      notes: 'Signed and acknowledged'
    },
    {
      id: 'CDOC-011',
      name: 'UCR Registration.pdf',
      folder: 'compliance',
      type: 'pdf',
      size: '345 KB',
      status: 'expiring',
      uploadedBy: 'Admin',
      uploadedAt: '2025-12-15',
      expiresAt: '2026-03-31',
      expirationDays: 11,
      notes: 'UCR renewal due end of Q1'
    },
    {
      id: 'CDOC-012',
      name: 'IFTA Registration.pdf',
      folder: 'compliance',
      type: 'pdf',
      size: '0 KB',
      status: 'missing',
      uploadedBy: null,
      uploadedAt: null,
      expiresAt: null,
      expirationDays: null,
      notes: 'Required if operating in multiple states'
    },
    // Driver Documents
    {
      id: 'CDOC-013',
      name: 'CDL - John Smith.pdf',
      folder: 'driver',
      type: 'pdf',
      size: '1.5 MB',
      status: 'uploaded',
      uploadedBy: 'Client',
      uploadedAt: '2026-02-10',
      expiresAt: '2028-05-15',
      expirationDays: 786,
      notes: 'Primary driver - Class A CDL'
    },
    {
      id: 'CDOC-014',
      name: 'Medical Card - John Smith.pdf',
      folder: 'driver',
      type: 'pdf',
      size: '890 KB',
      status: 'expiring',
      uploadedBy: 'Client',
      uploadedAt: '2025-04-20',
      expiresAt: '2026-04-20',
      expirationDays: 31,
      notes: 'DOT physical renewal needed'
    },
    {
      id: 'CDOC-015',
      name: 'MVR Report.pdf',
      folder: 'driver',
      type: 'pdf',
      size: '234 KB',
      status: 'pending',
      uploadedBy: null,
      uploadedAt: null,
      expiresAt: null,
      expirationDays: null,
      notes: 'Annual MVR check pending'
    },
    // Financial
    {
      id: 'CDOC-016',
      name: 'W-9 Form.pdf',
      folder: 'financial',
      type: 'pdf',
      size: '123 KB',
      status: 'uploaded',
      uploadedBy: 'Client',
      uploadedAt: '2026-01-05',
      expiresAt: null,
      expirationDays: null,
      notes: ''
    },
    {
      id: 'CDOC-017',
      name: 'Direct Deposit Authorization.pdf',
      folder: 'financial',
      type: 'pdf',
      size: '89 KB',
      status: 'uploaded',
      uploadedBy: 'Client',
      uploadedAt: '2026-01-05',
      expiresAt: null,
      expirationDays: null,
      notes: 'Verified bank info'
    }
  ]);

  // Calculate summary stats
  const summary = {
    total: documents.length,
    uploaded: documents.filter(d => d.status === 'uploaded').length,
    pending: documents.filter(d => d.status === 'pending').length,
    missing: documents.filter(d => d.status === 'missing').length,
    expiring: documents.filter(d => d.status === 'expiring').length
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesFolder = activeFolder === 'all' || doc.folder === activeFolder;
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
    return matchesFolder && matchesSearch && matchesStatus;
  });

  // Get file icon
  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-5 h-5 text-red-400" />;
      case 'doc': return <File className="w-5 h-5 text-blue-400" />;
      case 'image': return <FileImage className="w-5 h-5 text-green-400" />;
      default: return <File className="w-5 h-5 text-white/50" />;
    }
  };

  // Handle drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    setShowUploadModal(true);
  }, []);

  // Handle document delete (admin only)
  const handleDelete = (docId) => {
    if (!isAdmin) return;
    if (confirm('Are you sure you want to delete this document?')) {
      setDocuments(docs => docs.filter(d => d.id !== docId));
      setSelectedDocument(null);
    }
  };

  // Update document notes (admin only)
  const updateDocumentNotes = (docId, notes) => {
    setDocuments(docs => docs.map(d => 
      d.id === docId ? { ...d, notes } : d
    ));
    if (selectedDocument?.id === docId) {
      setSelectedDocument({ ...selectedDocument, notes });
    }
  };

  // Update document expiration
  const updateDocumentExpiration = (docId, expiresAt, expirationDays) => {
    setDocuments(docs => docs.map(d => 
      d.id === docId ? { ...d, expiresAt, expirationDays } : d
    ));
    if (selectedDocument?.id === docId) {
      setSelectedDocument({ ...selectedDocument, expiresAt, expirationDays });
    }
  };

  return (
    <div className="space-y-6" data-testid="client-document-vault">
      {/* Summary Banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {summary.missing > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 p-4 flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-2xl font-bold text-red-400">{summary.missing}</p>
              <p className="text-red-400/70 text-xs">Documents Missing</p>
            </div>
          </div>
        )}
        {summary.expiring > 0 && (
          <div className="bg-orange-500/10 border border-orange-500/30 p-4 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-orange-400 flex-shrink-0" />
            <div>
              <p className="text-2xl font-bold text-orange-400">{summary.expiring}</p>
              <p className="text-orange-400/70 text-xs">Expiring Soon</p>
            </div>
          </div>
        )}
        {summary.pending > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 flex items-center gap-3">
            <Clock className="w-8 h-8 text-yellow-400 flex-shrink-0" />
            <div>
              <p className="text-2xl font-bold text-yellow-400">{summary.pending}</p>
              <p className="text-yellow-400/70 text-xs">Pending Upload</p>
            </div>
          </div>
        )}
        <div className="bg-green-500/10 border border-green-500/30 p-4 flex items-center gap-3">
          <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0" />
          <div>
            <p className="text-2xl font-bold text-green-400">{summary.uploaded}</p>
            <p className="text-green-400/70 text-xs">Uploaded</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Folders */}
        <div className="lg:col-span-1 space-y-4">
          {/* Upload Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => setShowUploadModal(true)}
            className={`bg-[#0A0A0A] border-2 border-dashed p-4 text-center cursor-pointer transition-all ${
              dragOver ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/20 hover:border-white/40'
            }`}
            data-testid="client-upload-area"
          >
            <Upload className="w-6 h-6 text-[#D4AF37] mx-auto mb-1" />
            <p className="text-white text-sm font-medium">Upload Document</p>
            <p className="text-white/30 text-xs">Max 25MB</p>
          </div>

          {/* Folder List */}
          <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
            <div className="p-3 border-b border-white/10">
              <h4 className="text-white font-bold text-sm">Folders</h4>
            </div>
            <div className="divide-y divide-white/5">
              {CLIENT_FOLDERS.map((folder) => {
                const Icon = folder.icon;
                const count = folder.id === 'all' 
                  ? documents.length 
                  : documents.filter(d => d.folder === folder.id).length;
                return (
                  <button
                    key={folder.id}
                    onClick={() => setActiveFolder(folder.id)}
                    className={`w-full flex items-center justify-between p-3 transition-colors ${
                      activeFolder === folder.id ? 'bg-white/10' : 'hover:bg-white/5'
                    }`}
                    data-testid={`client-folder-${folder.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: folder.color }} />
                      <span className="text-white text-xs">{folder.name}</span>
                    </div>
                    <span className="text-white/40 text-xs">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-4">
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
                data-testid="client-vault-search"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]"
              data-testid="client-vault-status-filter"
            >
              <option value="all">All Status</option>
              <option value="uploaded">Uploaded</option>
              <option value="pending">Pending</option>
              <option value="missing">Missing</option>
              <option value="expiring">Expiring Soon</option>
            </select>
          </div>

          {/* Document List */}
          <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
            <div className="p-3 border-b border-white/10 flex justify-between items-center">
              <h4 className="text-white font-bold text-sm">
                {CLIENT_FOLDERS.find(f => f.id === activeFolder)?.name}
              </h4>
              <span className="text-white/50 text-xs">{filteredDocuments.length} files</span>
            </div>

            {filteredDocuments.length === 0 ? (
              <div className="p-8 text-center">
                <FolderOpen className="w-10 h-10 text-white/20 mx-auto mb-2" />
                <p className="text-white/50 text-sm">No documents found</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {filteredDocuments.map((doc) => {
                  const statusInfo = STATUS_CONFIG[doc.status];
                  const StatusIcon = statusInfo.icon;
                  return (
                    <div
                      key={doc.id}
                      className={`p-3 hover:bg-white/5 cursor-pointer transition-colors ${
                        selectedDocument?.id === doc.id ? 'bg-white/10' : ''
                      }`}
                      onClick={() => setSelectedDocument(doc)}
                      data-testid={`client-doc-${doc.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {getFileIcon(doc.type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{doc.name}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {doc.uploadedAt ? (
                                <span className="text-white/40 text-xs">{doc.size} • {doc.uploadedAt}</span>
                              ) : (
                                <span className="text-white/40 text-xs">Not uploaded</span>
                              )}
                              {doc.expiresAt && (
                                <span className={`text-xs flex items-center gap-1 ${
                                  doc.status === 'expiring' ? 'text-orange-400' : 'text-white/40'
                                }`}>
                                  <Calendar className="w-3 h-3" />
                                  {doc.expirationDays}d
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span 
                            className={`px-2 py-1 text-xs flex items-center gap-1 ${statusInfo.bg}`}
                            style={{ color: statusInfo.color }}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document Detail Sidebar */}
      {selectedDocument && (
        <div className="fixed inset-y-0 right-0 w-full sm:w-80 bg-[#0A0A0A] border-l border-white/10 z-50 overflow-y-auto">
          <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0A0A0A]">
            <h4 className="text-white font-bold text-sm">Document Details</h4>
            <button
              onClick={() => setSelectedDocument(null)}
              className="p-2 hover:bg-white/10 transition-colors"
              data-testid="close-client-doc-detail"
            >
              <X className="w-4 h-4 text-white/50" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* File Info */}
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white/5 rounded">
                {getFileIcon(selectedDocument.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium break-words">{selectedDocument.name}</p>
                <p className="text-white/40 text-xs">{selectedDocument.size}</p>
              </div>
            </div>

            {/* Status */}
            <div>
              <p className="text-white/40 text-xs uppercase mb-1">Status</p>
              <span 
                className={`px-3 py-1.5 text-sm flex items-center gap-2 w-fit ${STATUS_CONFIG[selectedDocument.status].bg}`}
                style={{ color: STATUS_CONFIG[selectedDocument.status].color }}
              >
                {(() => {
                  const Icon = STATUS_CONFIG[selectedDocument.status].icon;
                  return <Icon className="w-4 h-4" />;
                })()}
                {STATUS_CONFIG[selectedDocument.status].label}
              </span>
            </div>

            {/* Actions */}
            {selectedDocument.status === 'uploaded' || selectedDocument.status === 'expiring' ? (
              <div className="grid grid-cols-2 gap-2">
                <button 
                  className="flex items-center justify-center gap-2 p-2 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#B8962F] transition-colors"
                  data-testid="download-client-doc"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button className="flex items-center justify-center gap-2 p-2 bg-white/10 text-white text-sm hover:bg-white/20 transition-colors">
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setShowUploadModal(true)}
                className="w-full flex items-center justify-center gap-2 p-2 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#B8962F] transition-colors"
              >
                <Upload className="w-4 h-4" />
                Upload Document
              </button>
            )}

            {/* Expiration Date (Configurable) */}
            {isAdmin && (
              <div>
                <p className="text-white/40 text-xs uppercase mb-1">Expiration Date</p>
                <input
                  type="date"
                  value={selectedDocument.expiresAt || ''}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    const days = newDate ? Math.ceil((new Date(newDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                    updateDocumentExpiration(selectedDocument.id, newDate || null, days);
                  }}
                  className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]"
                />
                {selectedDocument.expiresAt && selectedDocument.expirationDays !== null && (
                  <p className={`mt-1 text-xs ${selectedDocument.expirationDays <= 30 ? 'text-orange-400' : 'text-white/40'}`}>
                    {selectedDocument.expirationDays > 0 
                      ? `Expires in ${selectedDocument.expirationDays} days`
                      : 'Expired'
                    }
                  </p>
                )}
              </div>
            )}

            {/* Upload Info */}
            {selectedDocument.uploadedAt && (
              <div>
                <p className="text-white/40 text-xs uppercase mb-1">Uploaded</p>
                <p className="text-white text-sm">{selectedDocument.uploadedAt}</p>
                <p className="text-white/50 text-xs">by {selectedDocument.uploadedBy}</p>
              </div>
            )}

            {/* Notes (Admin only can edit) */}
            <div>
              <p className="text-white/40 text-xs uppercase mb-1">Internal Notes</p>
              {isAdmin ? (
                <textarea
                  value={selectedDocument.notes}
                  onChange={(e) => updateDocumentNotes(selectedDocument.id, e.target.value)}
                  className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37] min-h-[80px] resize-none"
                  placeholder="Add internal notes..."
                />
              ) : (
                <p className="text-white/50 text-sm">{selectedDocument.notes || 'No notes'}</p>
              )}
            </div>

            {/* Delete (Admin only) */}
            {isAdmin && (selectedDocument.status === 'uploaded' || selectedDocument.status === 'expiring') && (
              <button
                onClick={() => handleDelete(selectedDocument.id)}
                className="w-full flex items-center justify-center gap-2 p-2 bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-colors mt-4"
                data-testid="delete-client-doc"
              >
                Delete Document
              </button>
            )}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#121212] border border-white/10 w-full max-w-md">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h4 className="text-white font-bold">Upload Document</h4>
              <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-white/10">
                <X className="w-4 h-4 text-white/50" />
              </button>
            </div>
            <div className="p-6">
              <div className="border-2 border-dashed border-white/20 p-6 text-center mb-4">
                <Upload className="w-10 h-10 text-[#D4AF37] mx-auto mb-2" />
                <p className="text-white text-sm mb-1">Drag files here or click to browse</p>
                <p className="text-white/40 text-xs">PDF, JPG, PNG, DOC up to 25MB</p>
                <button className="mt-3 px-4 py-2 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#B8962F] transition-colors">
                  Select Files
                </button>
              </div>
              
              {/* Folder Selection */}
              <div className="mb-4">
                <p className="text-white/40 text-xs uppercase mb-2">Upload to Folder</p>
                <select className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                  {CLIENT_FOLDERS.filter(f => f.id !== 'all').map(folder => (
                    <option key={folder.id} value={folder.id}>{folder.name}</option>
                  ))}
                </select>
              </div>

              <p className="text-white/40 text-xs text-center">
                Note: File upload is mocked. Connect object storage for real uploads.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDocumentVault;
